import {
  confirmPasswordReset,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  updatePassword,
} from 'firebase/auth';
import { auth } from 'firebase/firebase';

import { set } from '@src/packages/utils/storage';
import { apiCallable } from '@src/util/cloud-functions/apiClient';
import { getCallableDomainCode } from '@src/util/cloud-functions/callableDomainCode';
import { API_OPERATIONS } from '@src/util/cloud-functions/operations';
import safeExecute from '@src/util/safeExecute';

export default class AuthRepository {
  constructor() {
    this.errorObj = {
      'auth/invalid-email': 'Email invalido',
      'auth/user-disabled': 'El usuario esta deshabilitado',
      'auth/user-not-found': 'El usuario no existe',
      'auth/wrong-password': 'Clave incorrecta',
      'auth/internal-error': 'Error interno',
      'auth/invalid-custom-token': 'Token invalido',
      'auth/expired-action-code': 'El código ha expirado',
      'auth/invalid-action-code': 'El código es inválido',
      default: 'Error en la autenticación',
    };
  }

  async updateAndSignIn({ password, token, organizationId, user: userInput }) {
    try {
      const { user } = await signInWithCustomToken(auth, token);
      if (!user) throw new Error('Invalid token');

      if (userInput) {
        await AuthRepository.completeUserOnboarding({
          firstName: userInput.firstName,
          lastName: userInput.lastName,
          idCard: userInput.idCard,
          birthDate: userInput.birthDate,
          phone: userInput.phone,
          address: userInput.address,
          organizationId,
          jurisdictionId: userInput.jurisdictionId,
          uid: user.uid,
        });
      } else {
        await AuthRepository.completeUserOnboarding({ uid: user.uid });
      }

      let userLoggedIn = user;
      if (password) {
        await updatePassword(user, password);

        const { user: userLogged } = await signInWithEmailAndPassword(auth, user.email, password);
        userLoggedIn = userLogged;
      }

      set('user_id', userLoggedIn?.uid);
      window.location.href = '/';
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Invite acceptance for /invitationV2/:organizationId/:userId?t=… — the link secret (`token`)
   * buys a fresh custom token. The invite stays valid until onboarding succeeds, so a failed
   * submit can be retried from the same link.
   */
  async updateAndSignInV2({ password, organizationId, userId, token, user: userInput }) {
    try {
      const redeemData = await this.redeemUserInvite(organizationId, userId, token);
      const customToken = redeemData?.customToken ?? redeemData?.token;
      if (!customToken || typeof customToken !== 'string') {
        throw new Error('Invalid token');
      }

      const { user } = await signInWithCustomToken(auth, customToken);
      if (!user) throw new Error('Invalid token');

      await this.completeOnboardingAndSignIn({ user, password, organizationId, userInput });
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * /complete-registration — the invitee is already signed in (Google, password reset, email
   * link) but never completed the invite form.
   */
  async completeRegistration({ password, organizationId, user: userInput }) {
    try {
      const { currentUser } = auth;
      if (!currentUser) throw new Error('Not signed in');

      await this.completeOnboardingAndSignIn({
        user: currentUser,
        password,
        organizationId,
        userInput,
      });
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  async completeOnboardingAndSignIn({ user, password, organizationId, userInput }) {
    await AuthRepository.completeUserOnboarding({
      uid: user.uid,
      firstName: userInput?.firstName,
      lastName: userInput?.lastName,
      idCard: userInput?.idCard,
      birthDate: userInput?.birthDate,
      phone: userInput?.phone,
      flexTime: userInput?.flexTime,
      professionalEmail: userInput?.professionalEmail,
      timeZone: userInput?.timeZone,
      address: userInput?.address,
      organizationId,
      jurisdictionId: userInput?.jurisdictionId,
    });

    let userLoggedIn = user;
    if (password) {
      await updatePassword(user, password);
      const { user: userLogged } = await signInWithEmailAndPassword(auth, user.email, password);
      userLoggedIn = userLogged;
    }

    set('user_id', userLoggedIn?.uid);
    window.location.href = '/';
  }

  async redeemUserInvite(organizationId, userId, token) {
    const redeemFn = apiCallable(API_OPERATIONS.REDEEM_USER_INVITE);
    const response = await redeemFn({ organizationId, userId, token });
    return response.data;
  }

  async getInviteAcceptanceContext(organizationId, userId, token) {
    const inviteContextFn = apiCallable(API_OPERATIONS.INVITE_ACCEPTANCE_CONTEXT);
    const response = await inviteContextFn({ organizationId, userId, token });
    return response.data;
  }

  async getOnboardingContext(organizationId) {
    const onboardingContextFn = apiCallable(API_OPERATIONS.ONBOARDING_CONTEXT);
    const response = await onboardingContextFn(organizationId ? { organizationId } : {});
    return response.data;
  }

  async getApiKey(organizationId) {
    return safeExecute(async () => {
      const getApiKeyFn = apiCallable(API_OPERATIONS.CREATE_API_TOKEN);
      const response = await getApiKeyFn({ organizationId });
      return response.data;
    });
  }

  async createRestApiV2Credential(payload) {
    return safeExecute(async () => {
      const createCredentialFn = apiCallable(API_OPERATIONS.CREATE_REST_API_V2_CREDENTIAL);
      const response = await createCredentialFn(payload);
      return response.data;
    });
  }

  async createRestApiV2EventSubscription(payload) {
    return safeExecute(async () => {
      const createSubscriptionFn = apiCallable(
        API_OPERATIONS.CREATE_REST_API_V2_EVENT_SUBSCRIPTION,
      );
      const response = await createSubscriptionFn(payload);
      return response.data;
    });
  }

  static async completeUserOnboarding(data) {
    return await safeExecute(async () => {
      const completeUserOnboardingFn = apiCallable(API_OPERATIONS.COMPLETE_USER_ONBOARDING);
      const response = await completeUserOnboardingFn(data);
      return response.data;
    });
  }

  async signInWithEmailPassword(email, password) {
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      set('user_id', user.uid);
      return { success: true, uid: user.uid };
    } catch (error) {
      return { success: false, error };
    }
  }

  handleAuthError(error) {
    const mapped =
      this.errorObj[error?.code] ||
      this.errorObj.default ||
      (typeof error?.message === 'string' && error.message.trim()
        ? error.message
        : 'Error en la autenticación');
    const authError = new Error(mapped);
    // The generic copy above hides why the backend refused the call. Callers
    // that can translate a specific outcome need the original codes.
    authError.code = error?.code;
    const domainCode = getCallableDomainCode(error);
    if (domainCode) {
      authError.domainCode = domainCode;
    }
    return authError;
  }

  async requestPasswordReset(email) {
    return safeExecute(async () => {
      const sendPasswordResetFn = apiCallable(API_OPERATIONS.SEND_PASSWORD_RESET_EMAIL);
      const response = await sendPasswordResetFn({ email });
      return response.data;
    });
  }

  async resetPassword(code, newPassword) {
    return safeExecute(async () => {
      try {
        await confirmPasswordReset(auth, code, newPassword);
        return { success: true };
      } catch (error) {
        return { success: false, error: error.message };
      }
    }).catch(error => {
      throw error;
    });
  }

  async resendInvitation(organizationId, userId) {
    return safeExecute(async () => {
      const resendInvitationFn = apiCallable(API_OPERATIONS.RESEND_INVITATION);
      const response = await resendInvitationFn({ organizationId, userId });
      return response.data;
    });
  }

  async deleteInvitation(organizationId, userId) {
    return safeExecute(async () => {
      const deleteInvitationFn = apiCallable(API_OPERATIONS.DELETE_INVITATION);
      const response = await deleteInvitationFn({ organizationId, userId });
      return response.data;
    });
  }
}
